import { ArrowLeft, PenLine } from "lucide-react";
import { FormEvent, PointerEvent, useEffect, useRef, useState } from "react";
import { PortalFooter } from "../components/PortalFooter";
import { appConfig } from "../config/appConfig";
import { updatePassword } from "../lib/auth";
import { PASSWORD_REQUIREMENT_MESSAGE, passwordPolicyError } from "../lib/passwordPolicy";
import {
  completeRegistration,
  getRegistrationPrefill,
  requestRegistrationLink,
  type AccountProfile,
  type RegistrationPrefill
} from "../lib/portalApi";

type RegistrationPageProps = {
  onBackToLogin: () => void;
  onComplete: (profile: AccountProfile) => void;
};

type RegistrationForm = {
  firstName: string;
  lastName: string;
  phone: string;
  deaNumber: string;
  password: string;
  confirmPassword: string;
  signatureBase64: string;
};

type RegistrationField = keyof Pick<
  RegistrationForm,
  "firstName" | "lastName" | "phone" | "deaNumber" | "password" | "confirmPassword"
>;

type RegistrationFieldErrors = Partial<Record<RegistrationField, string>>;

const emptyForm: RegistrationForm = {
  firstName: "",
  lastName: "",
  phone: "",
  deaNumber: "",
  password: "",
  confirmPassword: "",
  signatureBase64: ""
};

function phoneDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

function formatPhone(value: string): string {
  const digits = phoneDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function deaLooksValid(value: string): boolean {
  const normalized = value.trim().toUpperCase();
  return !normalized || /^[A-Z]{2}\d{7}$/.test(normalized);
}

function apiErrorCode(error: unknown): string | null {
  const payload = (error as { payload?: unknown } | null)?.payload;
  if (payload && typeof payload === "object" && "error" in payload) {
    const value = (payload as { error?: unknown }).error;
    return typeof value === "string" ? value : null;
  }
  return null;
}

function authErrorMessage(error: unknown): string {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" ? message : "";
}

function authErrorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : "";
}

export function RegistrationPage({ onBackToLogin, onComplete }: RegistrationPageProps) {
  const [prefill, setPrefill] = useState<RegistrationPrefill | null>(null);
  const [form, setForm] = useState<RegistrationForm>(emptyForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<RegistrationFieldErrors>({});
  const [status, setStatus] = useState<string | null>(null);
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [requestState, setRequestState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [requestMessage, setRequestMessage] = useState<string | null>(null);
  const isDrawingRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    setError(null);

    getRegistrationPrefill()
      .then((nextPrefill) => {
        if (!isCurrent) return;
        setPrefill(nextPrefill);
        if (nextPrefill.profile) {
          setForm({
            firstName: nextPrefill.profile.firstName,
            lastName: nextPrefill.profile.lastName,
            phone: formatPhone(nextPrefill.profile.phone),
            deaNumber: nextPrefill.profile.deaNumber,
            password: "",
            confirmPassword: "",
            signatureBase64: ""
          });
          setRegistrationEmail(nextPrefill.profile.email);
        }
      })
      .catch(() => {
        if (isCurrent) setError("Registration could not be loaded. Please request a fresh registration link.");
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  function updateField<Key extends keyof RegistrationForm>(field: Key, value: RegistrationForm[Key]) {
    setForm((current) => ({ ...current, [field]: value }));
    if (field in fieldErrors) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[field as RegistrationField];
        return next;
      });
    }
  }

  function applyFieldErrors(nextErrors: RegistrationFieldErrors, fallbackMessage = "Fix the highlighted fields and try again.") {
    setFieldErrors(nextErrors);
    setError(fallbackMessage);
  }

  async function handleRegistrationLinkRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRequestMessage(null);
    const email = registrationEmail.trim().toLowerCase();
    if (!email) {
      setRequestState("failed");
      setRequestMessage("Enter the email address used for your registration.");
      return;
    }
    setRequestState("sending");
    try {
      const result = await requestRegistrationLink({ email });
      setRequestState("sent");
      setRequestMessage(result.message);
    } catch {
      setRequestState("failed");
      setRequestMessage("We could not send the request right now. Please try again or contact support.");
    }
  }

  function ensureCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width * window.devicePixelRatio));
    const height = Math.max(1, Math.floor(rect.height * window.devicePixelRatio));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(window.devicePixelRatio, window.devicePixelRatio);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.5;
    context.strokeStyle = "#16253f";
    return { canvas, context, rect };
  }

  function signaturePoint(event: PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  function startSignature(event: PointerEvent<HTMLCanvasElement>) {
    const canvasState = ensureCanvas();
    const point = signaturePoint(event);
    if (!canvasState || !point) return;
    isDrawingRef.current = true;
    canvasState.context.beginPath();
    canvasState.context.moveTo(point.x, point.y);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function drawSignature(event: PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) return;
    const canvasState = ensureCanvas();
    const point = signaturePoint(event);
    if (!canvasState || !point) return;
    canvasState.context.lineTo(point.x, point.y);
    canvasState.context.stroke();
    setHasDrawnSignature(true);
    updateField("signatureBase64", canvasState.canvas.toDataURL("image/png"));
  }

  function endSignature() {
    isDrawingRef.current = false;
  }

  function clearSignature() {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasDrawnSignature(false);
    updateField("signatureBase64", "");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setStatus(null);

    if (!prefill?.approved) {
      setError("This registration link is not approved. Please contact support for assistance.");
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim()) {
      applyFieldErrors({
        ...(!form.firstName.trim() ? { firstName: "First name is required." } : {}),
        ...(!form.lastName.trim() ? { lastName: "Last name is required." } : {})
      });
      return;
    }
    if (phoneDigits(form.phone).length !== 10) {
      applyFieldErrors({ phone: "Enter a 10-digit phone number." });
      return;
    }
    if (!deaLooksValid(form.deaNumber)) {
      applyFieldErrors({ deaNumber: "DEA number must use two letters followed by seven digits." });
      return;
    }
    const passwordError = passwordPolicyError(form.password);
    if (passwordError) {
      applyFieldErrors({ password: passwordError });
      return;
    }
    if (form.password !== form.confirmPassword) {
      applyFieldErrors({ confirmPassword: "Passwords do not match." });
      return;
    }

    setIsSaving(true);
    try {
      try {
        await updatePassword(form.password);
      } catch (passwordError) {
        const authMessage = authErrorMessage(passwordError).toLowerCase();
        const authCode = authErrorCode(passwordError).toLowerCase();
        if (authCode !== "same_password" && !authMessage.includes("different from the old password")) {
          throw passwordError;
        }
      }
      const result = await completeRegistration({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone,
        deaNumber: form.deaNumber.trim().toUpperCase() || undefined,
        signatureBase64: form.signatureBase64 || undefined
      });
      setStatus("Registration complete. Taking you to Submit Prescription...");
      onComplete(result.account);
    } catch (submitError) {
      const code = apiErrorCode(submitError);
      if (code === "invalid_dea_number") {
        applyFieldErrors({ deaNumber: "DEA number must use two letters followed by seven digits." });
        return;
      }
      if (code === "invalid_phone_number") {
        applyFieldErrors({ phone: "Enter a 10-digit phone number." });
        return;
      }
      if (code === "signature_too_small_or_blank") {
        setError("Please draw a slightly larger signature before completing registration, or clear it and add one later from Account.");
        return;
      }
      if (code === "signature_too_large" || code === "request_body_too_large") {
        setError("The signature image is too large. Please clear it and draw it again, or add it later from Account.");
        return;
      }
      if (code === "signature_invalid_base64" || code === "signature_invalid_png" || code === "signature_must_be_png") {
        setError("The signature image could not be read. Please clear it and draw it again, or add it later from Account.");
        return;
      }
      if (code === "registration_not_approved") {
        setError("This registration link is not approved or has expired. Please request a new access link.");
        return;
      }

      const authMessage = authErrorMessage(submitError).toLowerCase();
      const authCode = authErrorCode(submitError).toLowerCase();
      if (authMessage.includes("session") || authMessage.includes("expired") || authMessage.includes("token")) {
        setError("Your registration session has expired. Please open the latest registration email or request a new access link.");
        return;
      }
      if (authMessage.includes("weak") || authMessage.includes("strength") || authMessage.includes("easy to guess")) {
        applyFieldErrors({ password: `Choose a less common password. ${PASSWORD_REQUIREMENT_MESSAGE}` });
        return;
      }
      if (authMessage.includes("password")) {
        applyFieldErrors({ password: PASSWORD_REQUIREMENT_MESSAGE });
        return;
      }

      setError("Registration could not be completed right now. Please try again. If it continues, contact support.");
    } finally {
      setIsSaving(false);
    }
  }

  const approved = prefill?.approved === true;

  return (
    <main className="register-page">
      {appConfig.showTestBanner ? <div className="test-banner">TEST ENVIRONMENT</div> : null}

      <section className="register-shell">
        <button className="inline-back-button" type="button" onClick={onBackToLogin}>
          <ArrowLeft size={18} aria-hidden="true" />
          Back to sign in
        </button>

        <div className="register-grid registration-grid">
          <section className="register-intro" aria-labelledby="registration-heading">
            <img
              src="https://vp-brand-assets.s3.amazonaws.com/companion-pharmacy/companion_pharmacy_logo_primary.png"
              alt="Companion Pharmacy"
            />
            <p className="eyebrow">Approved Registration</p>
            <h1 id="registration-heading">Complete your portal registration</h1>
            <p>Confirm your prescriber details before your account is enabled for prescription workflows.</p>
            {prefill?.practice?.name ? (
              <div className="registration-practice-note">
                <span>Practice</span>
                <strong>{prefill.practice.name}</strong>
              </div>
            ) : null}
          </section>

          <section className="register-panel" aria-label="Complete registration form">
            {isLoading ? <div className="form-info">Loading approved registration details...</div> : null}

            {!isLoading && !approved ? (
              <div className="form-alert">
                <p>This registration link can no longer be used. If your account is active, sign in above. If you still need to finish registration, request another registration link and our team will review your account.</p>
                <form className="auth-link-request-form" onSubmit={handleRegistrationLinkRequest}>
                  <label htmlFor="registration-page-link-email">Email address</label>
                  <input
                    id="registration-page-link-email"
                    type="email"
                    autoComplete="email"
                    value={registrationEmail}
                    onChange={(event) => setRegistrationEmail(event.target.value)}
                    disabled={requestState === "sending" || requestState === "sent"}
                  />
                  {requestMessage ? (
                    <p className={requestState === "sent" ? "form-success" : "form-error"}>{requestMessage}</p>
                  ) : null}
                  <button className="primary-button" type="submit" disabled={requestState === "sending" || requestState === "sent"}>
                    {requestState === "sending" ? "Sending request..." : "Request another registration link"}
                  </button>
                </form>
              </div>
            ) : null}

            {approved ? (
              <form onSubmit={handleSubmit}>
                <div className="section-header">
                  <div>
                    <p className="eyebrow">Prescriber Details</p>
                    <h2>Confirm your information</h2>
                  </div>
                </div>

                <div className="two-column-form">
                  <label className={fieldErrors.firstName ? "field-invalid" : undefined}>
                    First name
                    <input
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={(event) => updateField("firstName", event.target.value)}
                      aria-invalid={fieldErrors.firstName ? "true" : undefined}
                      required
                    />
                    {fieldErrors.firstName ? <span className="error-text">{fieldErrors.firstName}</span> : null}
                  </label>
                  <label className={fieldErrors.lastName ? "field-invalid" : undefined}>
                    Last name
                    <input
                      autoComplete="family-name"
                      value={form.lastName}
                      onChange={(event) => updateField("lastName", event.target.value)}
                      aria-invalid={fieldErrors.lastName ? "true" : undefined}
                      required
                    />
                    {fieldErrors.lastName ? <span className="error-text">{fieldErrors.lastName}</span> : null}
                  </label>
                </div>

                <div className="two-column-form">
                  <label className={fieldErrors.phone ? "field-invalid" : undefined}>
                    Phone
                    <input
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
                      placeholder="(555) 555-5555"
                      value={form.phone}
                      onChange={(event) => updateField("phone", formatPhone(event.target.value))}
                      aria-invalid={fieldErrors.phone ? "true" : undefined}
                      required
                    />
                    {fieldErrors.phone ? <span className="error-text">{fieldErrors.phone}</span> : null}
                  </label>
                  <label>
                    Email
                    <input value={prefill?.profile?.email ?? ""} disabled />
                  </label>
                </div>

                <div className="two-column-form">
                  <label>
                    Veterinary medical board license number
                    <input value={prefill?.profile?.licenseNumber ?? ""} disabled />
                  </label>
                  <label>
                    State of licensure
                    <input value={prefill?.profile?.licenseState ?? ""} disabled />
                  </label>
                </div>

                <label className={fieldErrors.deaNumber ? "field-invalid" : undefined}>
                  DEA number
                  <input
                    autoCapitalize="characters"
                    value={form.deaNumber}
                    onChange={(event) => updateField("deaNumber", event.target.value.toUpperCase())}
                    aria-invalid={fieldErrors.deaNumber ? "true" : undefined}
                  />
                  {fieldErrors.deaNumber ? <span className="error-text">{fieldErrors.deaNumber}</span> : null}
                </label>

                <div className="two-column-form password-field-row">
                  <div className={`field-block${fieldErrors.password ? " field-invalid" : ""}`}>
                    <label htmlFor="registration-password">Password</label>
                    <input
                      id="registration-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      aria-describedby="registration-password-help"
                      value={form.password}
                      onChange={(event) => updateField("password", event.target.value)}
                      aria-invalid={fieldErrors.password ? "true" : undefined}
                      required
                    />
                    <span id="registration-password-help" className="field-help">
                      {PASSWORD_REQUIREMENT_MESSAGE}
                    </span>
                    {fieldErrors.password ? <span className="error-text">{fieldErrors.password}</span> : null}
                  </div>
                  <div className={`field-block${fieldErrors.confirmPassword ? " field-invalid" : ""}`}>
                    <label htmlFor="registration-confirm-password">Confirm password</label>
                    <input
                      id="registration-confirm-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={form.confirmPassword}
                      onChange={(event) => updateField("confirmPassword", event.target.value)}
                      aria-invalid={fieldErrors.confirmPassword ? "true" : undefined}
                      required
                    />
                    <span className="field-help">Re-enter the same password to confirm it.</span>
                    {fieldErrors.confirmPassword ? <span className="error-text">{fieldErrors.confirmPassword}</span> : null}
                  </div>
                </div>

                <div className="registration-signature">
                  <div>
                    <p className="eyebrow">Signature</p>
                    <h3>Optional signature setup</h3>
                    <p>Draw your signature now, or add it later from Account.</p>
                  </div>
                  <div className="signature-pad-shell registration-signature-pad">
                    {!hasDrawnSignature ? <span className="signature-pad-placeholder">Draw signature here</span> : null}
                    <canvas
                      ref={canvasRef}
                      className="signature-pad"
                      aria-label="Draw prescriber signature"
                      onPointerDown={startSignature}
                      onPointerMove={drawSignature}
                      onPointerLeave={endSignature}
                      onPointerUp={endSignature}
                    />
                  </div>
                  <button className="secondary-button compact-button" type="button" onClick={clearSignature}>
                    <PenLine size={16} aria-hidden="true" />
                    Clear signature
                  </button>
                </div>

                {status ? <div className="form-success">{status}</div> : null}
                {error ? <div className="form-alert">{error}</div> : null}

                <div className="button-row">
                  <button className="primary-button" type="submit" disabled={isSaving}>
                    {isSaving ? "Completing..." : "Complete registration"}
                  </button>
                  <button className="secondary-button" type="button" onClick={onBackToLogin}>
                    Back to sign in
                  </button>
                </div>
              </form>
            ) : null}
          </section>
        </div>
      </section>

      <PortalFooter />
    </main>
  );
}
